import React from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getLanguageCode} from 'modules/meta/selectors'

import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {NoStyleLink} from '@vio/atlas/src/components/designSystem/helpers/NoStyleLink'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/src/themes'
import {linariaMq} from '@vio/atlas/src/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {
  useGetBusinessUnitQuery,
  useGetReviewsQuery
} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {TrustpilotWidget} from '@vio/shared/src/search/TrustpilotWidget'
import {TRUSTPILOT_REVIEWS_URL} from '@vio/shared/src/search/TrustpilotWidget/constants'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo'

import {TRUSTPILOT_LABELS} from '../constants'
import {TrustpilotReviewItem} from '../TrustpilotReviewItem'
const POPOVER_WIDTH = 500

const Wrapper = styled.div`
  width: ${POPOVER_WIDTH}px;
`

const Header = styled.div`
  width: 100%;
  padding: ${cssTheme.layout.spacing.s600};
  background-color: ${cssTheme.colors.background.brand.primary};
  color: ${cssTheme.colors.content.neutral.inverse};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${cssTheme.layout.spacing.s400};
`
const BrandLogoWrapper = styled.div`
  box-shadow: ${cssTheme.shadows.floating};
  border-radius: ${cssTheme.layout.radius.lg};
`

const Body = styled.div`
  width: 100%;
  padding: ${cssTheme.layout.spacing.s600};
  height: calc(100vh - 400px);
  max-height: 450px;
  overflow-y: auto;
  overscroll-behavior: contain;
  ${linariaMq.desktopMd} {
    height: calc(100vh - 350px);
  }
`

const Footer = styled(NoStyleLink)`
  padding: ${cssTheme.layout.spacing.s600};
  padding-top: ${cssTheme.layout.spacing.s500};
  border-top: 1px solid;
  border-color: ${cssTheme.colors.border.neutral.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s200};
  width: 100%;
`

export const TrustpilotPopoverContent = () => {
  const {formatMessage} = useIntl()
  const dispatch = useDispatch()

  const languageCode = useSelector(getLanguageCode)
  const {data, isLoading} = useGetBusinessUnitQuery()
  const {data: reviewsData} = useGetReviewsQuery({
    language: languageCode
  })

  const {brand} = useBrand()

  const handleCallClickedEvent = (type: 'button' | 'count') => {
    if (type === 'count') {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.TrustpilotReviewsCount,
          action: Action.Clicked
        })
      )
    } else {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.TrustpilotAllReviewsButton,
          action: Action.Clicked
        })
      )
    }
  }

  return (
    <Wrapper>
      <Header>
        <BrandLogoWrapper>
          <BrandLogo height={50} width={50} brand={brand} compact />
        </BrandLogoWrapper>
        <Box flex="1">
          <Text variant="titleM">
            {formatMessage(TRUSTPILOT_LABELS.tensOfThousandsTrustVio)}
          </Text>
          <TrustpilotWidget
            isLoading={isLoading}
            variant="inline"
            theme="dark"
            numberOfReviews={data?.numberOfReviews?.total}
            stars={data?.score?.stars}
            trustScore={data?.score?.trustScore}
            onClick={() => handleCallClickedEvent('count')}
          />
        </Box>
      </Header>
      <Body>
        {reviewsData?.reviews?.map((review, index) => (
          <TrustpilotReviewItem
            review={review}
            key={review.id}
            hasDivider={index !== reviewsData.reviews.length - 1}
          />
        ))}
      </Body>
      <Footer
        onClick={() => handleCallClickedEvent('button')}
        href={TRUSTPILOT_REVIEWS_URL}
        target="_blank"
      >
        <Text variant="labelS">
          {formatMessage(TRUSTPILOT_LABELS.viewAllReviews)}
        </Text>
        <Icon name="Share" size="sm" />
      </Footer>
    </Wrapper>
  )
}
